import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToShortstayData,
  upsertShortstay,
} from "../../../../redux/shortstaysSlice";
import { Box, TextField, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const muiFieldSx = () => ({
  "& .MuiInputBase-input": {
    color: T.black,
    fontFamily: T.sans,
    fontSize: 13,
    textAlign: "center" as const,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    borderRadius: "10px",
    boxShadow: "none",
    "& fieldset": { borderColor: T.border, borderWidth: 1 },
    "&:hover fieldset": { borderColor: T.black },
    "&.Mui-focused fieldset": { borderColor: T.black, borderWidth: 1 },
  },
});

const stepBtnSx = {
  minWidth: 36,
  width: 36,
  height: 36,
  padding: 0,
  color: T.black,
  borderColor: T.border,
  borderRadius: "10px",
  "&:hover": { borderColor: T.black, background: "#F5F5F5" },
};

const ShortBasics: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector(
    (state: RootState) => state.shortstays.currentListing
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [form, setForm] = useState({
    guests: 1,
    bedrooms: 0,
    beds: 0,
    bathrooms: 0,
    toilets: 0,
    squareMeters: 0,
    parkingLots: 0,
    floorNumber: 0,
    minNights: 1,
    maxNights: 365,
  });

  // Load data
  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  // Prefill
  useEffect(() => {
    if (currentListing) {
      setForm({
        guests:
          Number(currentListing.guestCount || currentListing.guests) || 1,
        bedrooms: Number(currentListing.bedrooms) || 0,
        beds: Number(currentListing.beds) || 0,
        bathrooms: Number(currentListing.bathrooms) || 0,
        toilets: Number(currentListing.toilets) || 0,
        squareMeters: Number(currentListing.squareMeters) || 0,
        parkingLots: Number(currentListing.parkingLots) || 0,
        floorNumber: Number(currentListing.floorNumber) || 0,
        minNights: Number(currentListing.minNights) || 1,
        maxNights: Number(currentListing.maxNights) || 365,
      });
    }
  }, [currentListing]);

  // Change handler
  const handleChange = (key: string, value: string) => {
    const number = Math.max(0, parseInt(value) || 0);
    setForm((prev) => ({ ...prev, [key]: number }));
    setHasChanges(true);
  };

  // Step handler (+ / -)
  const handleStep = (key: string, diff: number) => {
    setForm((prev) => {
      const current = prev[key as keyof typeof form] as number;
      const stepSize = key === "squareMeters" ? 30 : 1;
      const next = Math.max(0, current + diff * stepSize);
      setHasChanges(true);
      return { ...prev, [key]: next };
    });
  };

  // Save handler
  const handleSave = async () => {
    if (!listingId || !hasChanges) return;
    try {
      setIsSaving(true);
      setSaved(false);
      setError(null);

      const payload = {
        ...form,
        guestCount: form.guests,
      };
      delete (payload as any).guests;

      await dispatch(upsertShortstay({ listingId, data: payload }));

      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError("Failed to save. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const fields = [
    { key: "minNights", label: "Minimum Nights Stay" },
    { key: "maxNights", label: "Maximum Nights Stay" },
    { key: "guests", label: "Guests" },
    { key: "bedrooms", label: "Bedrooms" },
    { key: "beds", label: "Beds" },
    { key: "bathrooms", label: "Bathrooms" },
    { key: "toilets", label: "Toilets (optional)" },
    { key: "squareMeters", label: "Square Meters (+30 increments)" },
    { key: "parkingLots", label: "Parking Lots" },
    { key: "floorNumber", label: "Floor Number" },
  ];

  return (
    <Box sx={{ background: T.bg, color: T.text, fontFamily: T.sans, maxWidth: 560, mx: "auto", position: "relative" }}>

      {/* SAVING OVERLAY */}
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(6px)",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: `3px solid ${T.border}`,
              borderTop: `3px solid ${T.black}`,
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p style={{ fontSize: 12, fontWeight: 500, color: T.black, letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
      )}

      {/* SUCCESS TOAST */}
      {saved && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #D4EDDA",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#22883F" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>Successfully saved</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>Basics updated</p>
          </div>
        </Box>
      )}

      {/* ERROR TOAST */}
      {error && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #F7C1C1",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#E24B4A" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#E24B4A", margin: 0 }}>Save failed</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>{error}</p>
          </div>
        </Box>
      )}

      {/* heading */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "28px" }}>
        <Box>
          <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.13em", textTransform: "uppercase", margin: "0 0 6px" }}>
            Property
          </p>
          <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>Basics</h2>
          <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
            Use + or – to adjust values. Zeros are okay.
          </p>
        </Box>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          sx={{
            background: hasChanges ? T.black : "#F0F0F0",
            color: hasChanges ? "#FFFFFF" : T.muted,
            border: "none",
            borderRadius: "10px",
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            textTransform: "none",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            boxShadow: hasChanges ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
            "&:hover": {
              background: hasChanges ? T.black : "#F0F0F0",
              boxShadow: hasChanges ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
            },
          }}
        >
          Save
        </Button>
      </Box>

      {/* form grid — flat, no card wrap */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {fields.map(({ key, label }) => (
          <Box key={key} sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: T.textSoft, letterSpacing: "0.04em" }}>
              {label}
            </label>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={() => handleStep(key, -1)}
                variant="outlined"
                sx={stepBtnSx}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </Button>

              <TextField
                type="number"
                value={(form as any)[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                fullWidth
                inputProps={{ min: 0 }}
                sx={muiFieldSx()}
              />

              <Button
                onClick={() => handleStep(key, 1)}
                variant="outlined"
                sx={stepBtnSx}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <p style={{ color: T.muted, fontSize: 12, textAlign: "center", marginTop: 30 }}>
        Keep your listing up to date — guests appreciate transparency.
      </p>
    </Box>
  );
};

export default ShortBasics;